import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { defaultRouteFor } from "@/pages/Login";
import { ArrowRight, Languages, ArrowDown } from "lucide-react";

// Real workshop / rug imagery references. Curated to feel like the source site.
const HERO = "https://images.unsplash.com/photo-1615529162924-f8605388461d?auto=format&fit=crop&w=2400&q=85";
const HERO_FALLBACK = "https://images.unsplash.com/photo-1569909115134-a0426936c879?auto=format&fit=crop&w=2400&q=85";
// Muted looping workshop video — self-hosted (uploaded by HAG). WebM VP9 primary for max codec coverage, MP4 H.264 fallback.
const HERO_VIDEO_WEBM = "/hero.webm";
const HERO_VIDEO_MP4 = "/hero.mp4";
const GALLERY = [
  { src: "https://images.unsplash.com/photo-1569909115134-a0426936c879?auto=format&fit=crop&w=1400&q=85", tall: false, alt: "Weaver at the loom" },
  { src: "https://images.unsplash.com/photo-1580041065738-e72023775cdc?auto=format&fit=crop&w=1400&q=85", tall: true, alt: "Coloured wool" },
  { src: "https://images.unsplash.com/photo-1652634213812-f0deeb1de78e?auto=format&fit=crop&w=1400&q=85", tall: false, alt: "Woven detail" },
  { src: "https://images.unsplash.com/photo-1531162805941-58330188d75c?auto=format&fit=crop&w=1400&q=85", tall: false, alt: "Warp threads" },
  { src: "https://images.unsplash.com/photo-1600166898405-da9535204843?auto=format&fit=crop&w=1400&q=85", tall: true, alt: "Finished rug" },
  { src: "https://images.unsplash.com/photo-1615873968403-89e068629265?auto=format&fit=crop&w=1400&q=85", tall: false, alt: "Village workshop" },
];

export default function Landing() {
  const { user } = useAuth();
  const { lang, toggle } = useLang();
  const nav = useNavigate();

  useEffect(() => {
    if (user) nav(defaultRouteFor(user.role));
  }, [user, nav]);

  return (
    <div style={{ background: "var(--brand-cream)", color: "var(--brand-navy)" }}>
      {/* ————————————————————————— TOP BAR ————————————————————————— */}
      <header className="absolute top-0 left-0 right-0 z-30">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-6">
          <div className="flex items-baseline gap-3 text-white">
            <span className="font-black text-2xl tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>HAG</span>
            <span className="hidden sm:inline text-[10px] tracking-[0.35em] uppercase text-white/70">Carpets · Est. 1979</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-[13px] uppercase tracking-[0.2em] text-white/70">
            <a href="#story" className="hover:text-white">Story</a>
            <a href="#craft" className="hover:text-white">Craft</a>
            <a href="#workshop" className="hover:text-white">Workshop</a>
            <a href="#ops" className="hover:text-white">Ops</a>
          </nav>
          <div className="flex items-center gap-2">
            <button
              data-testid="landing-lang-toggle"
              onClick={toggle}
              className="tap-target hidden sm:flex items-center gap-1 px-3 py-2 text-xs rounded-full text-white/85 hover:text-white border border-white/25"
            >
              <Languages size={14} /> {lang === "en" ? "हिन्दी" : "EN"}
            </button>
            <Link
              data-testid="landing-signin-btn"
              to="/login"
              className="tap-target px-5 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-semibold flex items-center gap-2"
              style={{ background: "var(--brand-gold)", color: "var(--brand-navy)" }}
            >
              Sign In <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </header>

      {/* ————————————————————————— HERO ————————————————————————— */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden" style={{ background: "#0f2338" }}>
        {/* Poster/still fallback rendered behind the video */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${HERO}), url(${HERO_FALLBACK})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "saturate(0.85) contrast(1.05)",
            zIndex: 0,
          }}
        />
        {/* Muted looping video */}
        <video
          data-testid="hero-video"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster={HERO}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "saturate(0.85) contrast(1.05)", zIndex: 1 }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        >
          <source src={HERO_VIDEO_WEBM} type="video/webm" />
          <source src={HERO_VIDEO_MP4} type="video/mp4" />
        </video>
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,35,56,0.55) 0%, rgba(15,35,56,0.15) 30%, rgba(15,35,56,0.85) 100%)", zIndex: 2 }} />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-28 w-full" style={{ zIndex: 3 }}>
          <div className="text-white/70 text-[10px] uppercase tracking-[0.4em] mb-6">Handcrafted Excellence · Mirzapur, India</div>
          <h1
            className="text-white leading-[0.92] tracking-tight max-w-5xl"
            style={{
              fontFamily: "'Fraunces', serif",
              fontWeight: 700,
              fontSize: "clamp(2.75rem, 8vw, 8rem)",
              letterSpacing: "-0.02em",
            }}
          >
            Rugs.<br />
            <span className="italic font-normal" style={{ color: "var(--brand-gold)" }}>It's what we do.</span><br />
            And we do it better<br />than anyone else.
          </h1>
          <div className="mt-10 flex flex-wrap items-center gap-6 text-white/80 text-sm">
            <a href="#story" className="flex items-center gap-2 border-b border-white/30 pb-1 hover:border-white">
              Discover the craft <ArrowDown size={14} />
            </a>
            <span className="text-white/40">·</span>
            <div className="text-white/60">Ops Phase 1 · Loom-to-ledger, live for the workshop floor</div>
          </div>
        </div>
      </section>

      {/* ————————————————————————— STORY (long-form paragraph) ————————————————————————— */}
      <section id="story" className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--brand-gold)" }}>Who we are</div>
          <div
            className="mt-6 text-2xl md:text-4xl leading-[1.35] max-w-4xl font-light"
            style={{ fontFamily: "'Fraunces', serif", color: "var(--brand-navy)" }}
          >
            We are a company rooted in strong traditional values and a penchant to spot the next niche in the rug industry. Over four decades we've partnered with wholesalers, retailers and designers across the globe to create rug lines that have gone on to be
            <em className="italic" style={{ color: "var(--brand-gold)" }}> best-sellers</em> in their categories.
          </div>
          <p className="mt-6 text-[#4A627A] max-w-3xl leading-relaxed">
            We believe in being more than just a vendor to our clients. We invest our expertise, understanding and money into the R&amp;D that goes into creating trend-setting product lines. We revel in the discovery of new shades, patterns and textures. It's this passion that inspires us each day. Come explore the endless possibilities.
          </p>
        </div>
      </section>

      {/* ————————————————————————— DESIGN · CREATE · INSPIRE ————————————————————————— */}
      <section id="craft" className="py-20 md:py-28 relative" style={{ background: "#F2ECE0" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-20">
            <Pillar
              n="01"
              title="Design"
              body="In 47 years of craftsmanship we have created thousands of designs loved by millions of patrons worldwide. The design process is bespoke — an endeavour to hand-craft carpets that inspire."
            />
            <Pillar
              n="02"
              title="Create"
              body="We hand-craft quality rugs made to last, and to inspire, generation to generation. Our traditional way of making carpets has not changed in four decades — and we intend to preserve this art in its root form."
            />
            <Pillar
              n="03"
              title="Inspire"
              body="The rooted form of this art — hand-drawn design, hand-woven, hand-washed, hand-dried — while the world races to machines. Everything about it will inspire you."
            />
          </div>
        </div>
      </section>

      {/* ————————————————————————— GALLERY (justified image grid) ————————————————————————— */}
      <section id="workshop" className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--brand-gold)" }}>The workshop</div>
              <h2
                className="mt-3 text-3xl md:text-5xl leading-tight"
                style={{ fontFamily: "'Fraunces', serif", color: "var(--brand-navy)", fontWeight: 600 }}
              >
                47 <span className="italic font-normal" style={{ color: "var(--brand-gold)" }}>years</span> of handcrafting excellence.
              </h2>
            </div>
            <div className="text-sm text-[#4A627A] max-w-sm">
              Every carpet begins in Mirzapur — one of India's oldest weaving belts — on 46 active looms across three zones.
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
            {GALLERY.map((g, i) => (
              <div
                key={i}
                className={`overflow-hidden rounded-md ${g.tall ? "row-span-2 md:h-[520px]" : "h-[240px] md:h-[250px]"}`}
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  style={{ transition: "transform 0.6s ease" }}
                  onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.03)")}
                  onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ————————————————————————— OPS LAYER (three columns) ————————————————————————— */}
      <section id="ops" className="py-24 md:py-32" style={{ background: "var(--brand-navy)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--brand-gold)" }}>The ops layer</div>
          <h2
            className="mt-3 text-white text-3xl md:text-5xl leading-tight max-w-4xl"
            style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
          >
            Loom‑to‑ledger, from Mirzapur.
          </h2>
          <p className="mt-6 text-white/70 max-w-2xl leading-relaxed">
            Two decades of scribbled ledgers, WhatsApp voice notes and shared Excel — replaced by one calm dashboard. Without changing how a single knot is tied.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16 mt-14">
            <OpsCol h="Who we are" body="A 47‑year‑old family of weavers, designers and merchants in Bhadohi–Mirzapur. Fifteen master weavers. Eight production units. One quiet workflow." />
            <OpsCol h="How we do it" body="Six‑stage weaver POs, offline‑first Daura inspections with GPS‑tagged photos, and grade‑locked history that revises but never deletes." />
            <OpsCol h="Get in touch" body="Sign in with your role — inspector, supervisor, coordinator, merchandiser, or director. English or Hindi. Any 6‑inch Android." cta />
          </div>
        </div>
      </section>

      {/* ————————————————————————— FOOTER ————————————————————————— */}
      <footer className="py-10" style={{ background: "#0d1f34" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-white/60 text-xs">
          <div className="flex items-baseline gap-2">
            <span className="font-black text-white text-lg" style={{ fontFamily: "'Fraunces', serif" }}>HAG</span>
            <span className="text-white/50 uppercase tracking-[0.25em] text-[10px]">Carpets Pvt. Ltd.</span>
          </div>
          <div className="uppercase tracking-[0.3em] text-[10px]">All rights reserved · Ops Phase 1</div>
        </div>
      </footer>
    </div>
  );
}

function Pillar({ n, title, body }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.4em]" style={{ color: "var(--brand-gold)" }}>{n}</div>
      <h3
        className="mt-3 text-3xl md:text-4xl"
        style={{ fontFamily: "'Fraunces', serif", color: "var(--brand-navy)", fontWeight: 600 }}
      >
        {title}
      </h3>
      <div className="mt-4 h-px w-14" style={{ background: "var(--brand-gold)" }} />
      <p className="mt-4 text-[#4A627A] leading-relaxed">{body}</p>
    </div>
  );
}

function OpsCol({ h, body, cta }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.35em]" style={{ color: "var(--brand-gold)" }}>{h}</div>
      <div className="mt-3 h-px w-10" style={{ background: "var(--brand-gold)", opacity: 0.6 }} />
      <p className="mt-5 text-white/80 leading-relaxed text-[15px]">{body}</p>
      {cta && (
        <Link
          to="/login"
          data-testid="ops-signin-btn"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.25em] font-semibold"
          style={{ background: "var(--brand-gold)", color: "var(--brand-navy)" }}
        >
          Enter Operations <ArrowRight size={12} />
        </Link>
      )}
    </div>
  );
}
